import { Facility, UnitGroup } from "@/types";

type FlattenValue = string | number | boolean | null | undefined;

function flattenObject(
    obj: Record<string, unknown>,
    prefix = "",
): Record<string, FlattenValue> {
    return Object.keys(obj).reduce((acc, key) => {
        const value = obj[key];
        const newKey = prefix ? `${prefix}.${key}` : key;

        if (value === null || value === undefined) {
            // Explicitly handle null and undefined
            acc[newKey] = value;
        } else if (Array.isArray(value)) {
            // Handle arrays
            if (value.length === 0) {
                // Preserve empty arrays
                acc[newKey] = null;
            } else {
                value.forEach((item, index) => {
                    if (item !== null && typeof item === "object") {
                        // Recursively flatten objects in arrays
                        const flattened = flattenObject(
                            item as Record<string, unknown>,
                            `${newKey}[${index}]`,
                        );
                        Object.assign(acc, flattened);
                    } else {
                        acc[`${newKey}[${index}]`] = item as FlattenValue;
                    }
                });
            }
        } else if (typeof value === "object") {
            // Handle nested objects (but not null, already handled above)
            const flattened = flattenObject(value as Record<string, unknown>, newKey);
            Object.assign(acc, flattened);
        } else {
            // Handle primitive values (string, number, boolean)
            acc[newKey] = value as FlattenValue;
        }

            return acc;
        },
        {} as Record<string, FlattenValue>,
    );
}

type Promo = {
    id: number;
};

interface TranslateSources {
    unitGroup: UnitGroup;
    facility?: Facility;
    promo?: Promo;
}

export function translateWithMultipleSources(
    template: string,
    sources: TranslateSources,
    selectedUnitId: number,
    selectedPromoId: number,
): string {
    const selectedUnit = sources.unitGroup.available_units.find(
        (unit) => unit.id === selectedUnitId,
    );
    const selectedPromo = sources.unitGroup.rates.promo_rates.find(
        (promo) => promo.id === selectedPromoId,
    );

    if (selectedUnit) {
        sources.unitGroup = {
            ...sources.unitGroup,
            representative_unit: selectedUnit,
            rates: {
                ...sources.unitGroup.rates,
                promo_rates: selectedPromo
                    ? [selectedPromo]
                    : sources.unitGroup.rates.promo_rates,
            },
        };
    }
    // Flatten all data sources
    const flatData = Object.keys(sources).reduce(
        (acc, key) => {
            const source = sources[key as keyof TranslateSources];
            if (source) {
                Object.assign(acc, flattenObject(source as Record<string, unknown>, key));
            }
            return acc;
        },
        {} as Record<string, FlattenValue>,
    );

    // Replace placeholders with actual data
    return template.replace(/{{(.*?)}}/g, (_, key) => {
        const value = flatData[key];
        return value !== undefined ? String(value) : `{{${key}}}`;
    });
}
